import type { TileField, TileFieldHiddenRecord, TileFieldVisible, TileRecordMatch, TileRecordValueType } from "@fortex/api";

export { dateInputValue, objectTitle, parseBool, translationValue, visibleFields } from "../info/helpers";

export function prettyJson(value: string | null): string {
  if (value === null || value.trim().length === 0) return "";

  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

export function isValidJson(value: string): boolean {
  if (value.trim().length === 0) return true;

  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

export type PhotoRef = { id: string; raw: string };

export type JsonRecord = { key: string; value: string }[];

const photoKeyPattern = /kep|photo|image|picture|foto/i;

function parseJson(value: string | null): unknown {
  if (value === null || value.trim().length === 0) return null;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function scalarText(raw: unknown): string {
  if (raw === null || raw === undefined) return "";
  if (typeof raw === "object") return JSON.stringify(raw);

  return String(raw);
}

export function photoId(raw: string): string {
  const normalised = raw.trim().replace(/\\/g, "/");
  const index = normalised.lastIndexOf("/");

  return index >= 0 ? normalised.slice(index + 1) : normalised;
}

export function photoRefs(value: string | null): PhotoRef[] | null {
  const parsed = parseJson(value);

  if (!Array.isArray(parsed) || parsed.length === 0) return null;

  const refs: PhotoRef[] = [];

  for (const item of parsed) {
    if (!isPlainRecord(item)) return null;

    const keys = Object.keys(item);

    if (keys.length !== 1 || keys[0] !== "value" || typeof item.value !== "string" || item.value.trim().length === 0) return null;

    refs.push({ id: photoId(item.value), raw: item.value });
  }

  return refs;
}

export function isPhotoField(key: string, value: string | null): boolean {
  if (photoRefs(value) !== null) return true;
  if (!photoKeyPattern.test(key)) return false;

  const parsed = parseJson(value);

  return parsed === null || (Array.isArray(parsed) && parsed.length === 0);
}

export function photoJson(ids: string[]): string {
  return JSON.stringify(ids.map(a => ({ value: a })));
}

export function jsonRecords(value: string | null): JsonRecord[] | null {
  const parsed = parseJson(value);
  const items = Array.isArray(parsed) ? parsed : isPlainRecord(parsed) ? [parsed] : null;

  if (items === null || items.length === 0 || !items.every(isPlainRecord)) return null;

  return items.map(item => Object.entries(item).map(([key, raw]) => ({ key, value: scalarText(raw) })));
}

const recordMatchers: Record<TileRecordMatch, (text: string, wanted: string) => boolean> = {
  EndsWith: (text, wanted) => text.endsWith(wanted),
  StartsWith: (text, wanted) => text.startsWith(wanted),
  Contains: (text, wanted) => text.includes(wanted),
  Equals: (text, wanted) => text === wanted
};

function folded(value: string | null | undefined): string {
  return (value ?? "").trim().toLocaleLowerCase();
}

function visibleTo(visible: TileFieldVisible | null | undefined, admin: boolean): boolean {
  return visible === "All" || (visible === "Admin" && admin);
}

function recordValue(record: JsonRecord, key: string): string {
  return record.find(a => a.key === key)?.value ?? "";
}

export function recordMatches(value: string, rule: TileFieldHiddenRecord): boolean {
  const wanted = folded(rule.value);
  const matcher = recordMatchers[rule.match];

  return wanted.length > 0 && matcher !== undefined && matcher(folded(value), wanted);
}

export function dateTimeValue(value: string): number | null {
  const match = /^(\d{4})[.\-/]\s?(\d{1,2})[.\-/]\s?(\d{1,2})\.?(?:[ T]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/.exec(value.trim());

  if (match === null) return null;

  const [year, month, day, hour, minute, second] = match.slice(1).map(a => Number(a ?? "0"));

  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59 || second > 59) return null;

  return Date.UTC(year, month - 1, day, hour, minute, second);
}

export function numberValue(value: string): number | null {
  const text = value.trim().replace(/\s/g, "").replace(",", ".");

  if (text.length === 0) return null;

  const parsed = Number(text);

  return Number.isFinite(parsed) ? parsed : null;
}

export function sortValue(value: string, type: TileRecordValueType): string | number | null {
  const text = value.trim();

  if (text.length === 0) return null;
  if (type === "DateTime") return dateTimeValue(text);
  if (type === "Number") return numberValue(text);

  return text;
}

function compareSortValues(a: string | number, b: string | number): number {
  if (typeof a === "number" && typeof b === "number") return a - b;

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

export function displayRecords(records: JsonRecord[], field: TileField, admin: boolean, searchTerm: string | null = null): JsonRecord[] {
  const rules = (field.hiddenRecords ?? []).filter(a => a !== null && typeof a.key === "string" && a.key.length > 0);
  const orders = (field.displayOrder ?? []).filter(a => a !== null && typeof a.key === "string" && a.key.length > 0);
  const shown = records.filter(record => !rules.some(rule => !visibleTo(rule.visible, admin) && recordMatches(recordValue(record, rule.key), rule)));
  const keyed = shown.map((record, index) => ({ record, index, values: orders.map(order => sortValue(recordValue(record, order.key), order.type)) }));
  const sorted = [...keyed].sort((a, b) => {
    for (let i = 0; i < orders.length; i++) {
      const left = a.values[i];
      const right = b.values[i];

      if (left === null && right === null) continue;
      if (left === null) return 1;
      if (right === null) return -1;

      const compared = compareSortValues(left, right);

      if (compared !== 0) return orders[i].direction === "Descending" ? -compared : compared;
    }

    return a.index - b.index;
  });
  const term = folded(searchTerm);

  if (term.length === 0) return sorted.map(a => a.record);

  const matching = (record: JsonRecord) => record.some(pair => folded(pair.value).startsWith(term));

  return [...sorted.filter(a => matching(a.record)), ...sorted.filter(a => !matching(a.record))].map(a => a.record);
}

export function machineCoordinates(lat: number, lon: number): string {
  return `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
}

export async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    return false;
  }

  return false;
}
